import type React from "react";
import type { BooksSearchSchema } from "./search";

import { useForm } from "@tanstack/react-form";
import { Link, useRouter, useSearch } from "@tanstack/react-router";
import { FilterIcon, SearchIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const FiltersBar: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-grow items-center gap-2">
          <Search />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-grow items-center gap-2">
          <Filters />
          <ActiveFilters />
        </div>
      </div>
    </div>
  );
};

const Search: React.FC = () => {
  const { t } = useTranslation();
  const search: BooksSearchSchema = useSearch({ from: "/books" });

  const router = useRouter();

  const form = useForm({
    defaultValues: {
      q: search.q || ""
    },
    onSubmit: async ({ value }) => {
      router.navigate({
        to: "/books",
        search: {
          ...search,
          q: value.q || undefined,
          page: undefined
        }
      });
    }
  });

  useEffect(() => {
    form.setFieldValue("q", search.q || "");
  }, [form, search.q]);

  return (
    <div className="dui-form-control max-w-xs flex-grow">
      <form
        role="search"
        className="mx-auto w-full max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <label htmlFor="site-search" className="sr-only">
          {t("books.filters.search.label")}
        </label>
        <div className="flex gap-2">
          <form.Field name="q">
            {(field) => (
              <input
                type="search"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="dui-input dui-input-bordered w-full"
                placeholder={t("books.filters.search.placeholder")}
                aria-label="Search"
              />
            )}
          </form.Field>
          <button type="submit" className="dui-btn dui-btn-primary flex items-center gap-2">
            <SearchIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t("books.filters.search.submit")}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

const Filters: React.FC = () => {
  const { t } = useTranslation();

  const search: BooksSearchSchema = useSearch({ from: "/books" });
  const activeFilters = [search.read, search.owned, search.tbr].filter((v) => v !== undefined).length;

  const readFilters = [undefined, "read", "reading", "unread"];
  const ownedFilters = [undefined, true, false];
  const tbrFilters = [undefined, true];

  return (
    <div className="dui-dropdown">
      <div tabIndex={0} role="button" className="dui-btn">
        <FilterIcon className="h-4 w-4" aria-hidden="true" />
        Filters
        <div className="dui-badge dui-badge-secondary dui-badge-xs">{activeFilters}</div>
      </div>

      <div className="dui-dropdown-content dui-card dui-card-sm bg-base-100 z-1 w-64 shadow-md">
        <div className="dui-card-body">
          <div className="font-medium">{t("books.filters.filters.read.label")}</div>
          <div className="btn-group w-full">
            {readFilters.map((filter) => (
              <Link
                key={filter}
                to="/books"
                search={{ ...search, read: filter }}
                className={`dui-btn dui-btn-xs ${search.read === filter ? "dui-btn-active dui-btn-primary" : ""}`}
              >
                {filter === undefined
                  ? t("books.filters.filters.common.any")
                  : t(`books.filters.filters.read.${filter}`)}
              </Link>
            ))}
          </div>

          <div className="font-medium">{t("books.filters.filters.owned.label")}</div>
          <div className="btn-group w-full">
            {ownedFilters.map((filter) => (
              <Link
                key={(filter ?? "undefined").toString()}
                to="/books"
                search={{ ...search, owned: filter }}
                className={`dui-btn dui-btn-xs ${search.owned === filter ? "dui-btn-active dui-btn-primary" : ""}`}
              >
                {filter === undefined
                  ? t("books.filters.filters.common.any")
                  : t(`books.filters.filters.owned.${filter}`)}
              </Link>
            ))}
          </div>

          <div className="font-medium">{t("books.filters.filters.tbr.label")}</div>
          <div className="btn-group w-full">
            {tbrFilters.map((filter) => (
              <Link
                key={(filter ?? "undefined").toString()}
                to="/books"
                search={{ ...search, tbr: filter }}
                className={`dui-btn dui-btn-xs ${search.tbr === filter ? "dui-btn-active dui-btn-primary" : ""}`}
              >
                {filter === undefined
                  ? t("books.filters.filters.common.any")
                  : t(`books.filters.filters.tbr.${filter}`)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ActiveFilters: React.FC = () => {
  const { t } = useTranslation();
  const search: BooksSearchSchema = useSearch({ from: "/books" });

  const readingFilters: Record<string, string> = {
    read: "books.filters.filters.read.read",
    reading: "books.filters.filters.read.reading",
    unread: "books.filters.filters.read.unread"
  };
  const readingFilter = readingFilters[search.read ?? ""];

  const ownedFilters: Record<string, string> = {
    true: "books.filters.filters.owned.true",
    false: "books.filters.filters.owned.false"
  };
  const ownedFilter = ownedFilters[search.owned?.toString() ?? ""];

  const tbrFilters: Record<string, string> = {
    true: "books.filters.filters.tbr.true",
    false: "books.filters.filters.tbr.false"
  };
  const tbrFilter = tbrFilters[search.tbr?.toString() ?? ""];

  return (
    <div className="hidden flex-wrap gap-2 md:flex">
      {readingFilter !== undefined && <div className="dui-badge dui-badge-outline gap-1">{t(readingFilter)}</div>}
      {tbrFilter !== undefined && <div className="dui-badge dui-badge-outline gap-1">{t(tbrFilter)}</div>}
      {ownedFilter !== undefined && <div className="dui-badge dui-badge-outline gap-1">{t(ownedFilter)}</div>}
    </div>
  );
};
